import type { DeckZone, DeckZoneInput } from "@/interfaces/DeckIdentity";
import type { GameHandler } from "@/interfaces/GameHandler";

/**
 * The zones a deck has when its game declares nothing special: the main deck,
 * plus a sideboard whenever the decklist actually carries one.
 *
 * Sideboard support is deliberately generic - it is a property of the decklist,
 * not of any one game.
 */
/**
 * Sorting an empty card list yields a malformed "NaNxundefined" entry, so
 * never hand an empty string to a game's sortDecklist.
 */
export function sortCards(
  handler: GameHandler,
  cards: string | null | undefined,
  identityId: string,
): string {
  const list = (cards ?? "").trim();
  return list ? handler.sortDecklist(list, identityId) : "";
}

export function defaultDeckZones(
  handler: GameHandler,
  deck: DeckZoneInput,
): DeckZone[] {
  const zones: DeckZone[] = [
    {
      id: "main",
      cards: sortCards(handler, deck.decklist, deck.identityId),
    },
  ];

  if (deck.sideboard) {
    zones.push({
      id: "sideboard",
      label: "Sideboard",
      cards: sortCards(handler, deck.sideboard, deck.identityId),
    });
  }

  return zones;
}

/** Zones for a deck, using the game's own split when it defines one. */
export function getDeckZones(
  handler: GameHandler,
  deck: DeckZoneInput,
): DeckZone[] {
  const zones = handler.getDeckZones
    ? handler.getDeckZones(deck)
    : defaultDeckZones(handler, deck);

  // A zone with nothing in it should not render a heading.
  return zones.filter((z) => zoneCardIds(z).length > 0);
}

/** Card ids in a zone, in order. */
export function zoneCardIds(zone: DeckZone): string[] {
  return zone.cards
    .split(";")
    .filter(Boolean)
    .map((entry) => entry.split("x")[1])
    .filter(Boolean);
}
